import React from 'react';
import './VirualTable.css';

export default function VirtualTable({
  tableData = [],
  header = [],
  headerRowHeight,
  HeaderRender,
  RowRender,
  columnWidth,
  rowHeight,
  fixedColumnCount = 0,
  onFetch = null,
  isFetch = false,
  Loader = null,
  TableLegend = null,
}) {
  const ref = React.useRef();
  const intersectionRef = React.useRef(null);
  const [visibleRows, setVisibleRows] = React.useState(0);
  const [visibleColumns, setVisibleColumns] = React.useState(0);
  const [firstRowIndex, setFirstRowIndex] = React.useState(0);
  const [firstColumnIndex, setFirstColumnIndex] = React.useState(0);

  const [offsetTop, setOffsetTop] = React.useState(0);
  const [offsetLeft, setOffsetLeft] = React.useState(0);

  const [scrollTableWidth, setScrollTableWidth] = React.useState(0);
  const [scrollTableHeight, setScrollTableHeight] = React.useState(0);

  const getFixedData = (arr, fixedCount) => {
    if (!fixedCount) return [];
    return arr.slice(0, fixedCount);
  };

  const visibleData = React.useMemo(() => {
    const head = [...getFixedData(header, fixedColumnCount), ...header.slice(firstColumnIndex + fixedColumnCount, visibleColumns + firstColumnIndex + 1)];
    const data = tableData.slice(firstRowIndex, visibleRows + firstRowIndex + 1).map((row) => [...getFixedData(row, fixedColumnCount), ...row.slice(firstColumnIndex + fixedColumnCount, visibleColumns + firstColumnIndex + 1)]);
    return {
      head,
      data,
    };
  }, [tableData, firstRowIndex, visibleRows, firstColumnIndex, visibleColumns, header, fixedColumnCount]);

  const onIntersection = React.useCallback((entries) => {
    if (entries[0].intersectionRatio <= 0) return;
    if (onFetch && intersectionRef.current) {
      intersectionRef.current = null;
      onFetch();
    }
  }, [onFetch]);

  const options = {
    root: null,
    rootMargin: '0px',
    threshold: 1.0,
  };

  React.useEffect(() => {
    const observer = new IntersectionObserver(onIntersection, options);
    if (intersectionRef.current) observer.observe(intersectionRef.current);

    return () => {
      if (intersectionRef.current) observer.unobserve(intersectionRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options, onIntersection, intersectionRef.current]);

  const onScroll = (e) => {
    const { scrollTop, scrollLeft } = e.target;
    const rowIndex = Math.floor(scrollTop / rowHeight);
    setOffsetLeft(scrollLeft);
    setOffsetTop(scrollTop);
    setFirstRowIndex(rowIndex);
    setFirstColumnIndex(Math.floor(scrollLeft / columnWidth));
  };

  const calculateTableParams = React.useCallback(() => {
    const { parentNode } = ref.current;
    setVisibleRows(Math.floor((parentNode.clientHeight - headerRowHeight) / rowHeight));
    setVisibleColumns(Math.floor(parentNode.clientWidth / columnWidth));
    const tableWidth = header.length * columnWidth + columnWidth;
    const tableHeight = tableData.length * rowHeight + (headerRowHeight + rowHeight) + 100;
    setScrollTableWidth(tableWidth > parentNode.clientWidth ? tableWidth : parentNode.clientWidth);
    setScrollTableHeight(tableHeight);
  }, [columnWidth, header.length, tableData.length, headerRowHeight, rowHeight]);

  React.useEffect(() => {
    if (ref.current) {
      calculateTableParams();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref.current?.parentNode?.clientHeight, ref.current?.parentNode?.clientWidth, calculateTableParams]);

  React.useEffect(() => {
    setScrollTableHeight((cur) => {
      if (isFetch) {
        return cur + 250;
      }
      return cur - 250;
    });
  }, [isFetch]);

  React.useEffect(() => {
    if (tableData.length === 0) {
      setOffsetLeft(0);
      setOffsetTop(0);
      setFirstRowIndex(0);
      setFirstColumnIndex(0);
    }
  }, [tableData.length]);

  React.useEffect(() => {
    function onResize() {
      calculateTableParams();
    }

    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, [calculateTableParams]);

  return (
    <div className="table-wrapper" onScroll={onScroll}>
      <div
        ref={ref}
        className="table-scroll-element"
        style={{ width: scrollTableWidth, height: scrollTableHeight }}
      >
        <table
          className="table"
          style={{
            top: offsetTop,
            left: offsetLeft,
          }}
        >
          <thead className="table-head">
            {TableLegend && <TableLegend firstColumnIndex={firstColumnIndex} visibleColumns={visibleColumns} />}
            <tr>
              {React.Children.toArray(visibleData.head.map((item, itemIndex) => (
                <th
                  className={`${itemIndex < fixedColumnCount ? 'fixed-column' : ''} cell`}
                  style={{
                    width: columnWidth,
                    height: headerRowHeight,
                  }}
                >
                  <HeaderRender item={item} rowIndex={0} columnIndex={itemIndex + firstColumnIndex} />
                </th>
              )))}
            </tr>
          </thead>
          <tbody>
            {React.Children.toArray(visibleData.data.map((row, index) => (
              <tr ref={(r) => {
                const load = Math.floor(tableData.length * 0.8);
                if (index + firstRowIndex >= load) {
                  intersectionRef.current = r;
                }
              }}
              >
                {React.Children.toArray(row.map((item, itemIndex) => (
                  <td
                    className={`${itemIndex < fixedColumnCount ? 'fixed-column' : ''} cell`}
                    style={{ textAlign: 'center', height: rowHeight }}
                  >
                    <RowRender item={item} headers={visibleData.head[itemIndex]} rows={visibleData.data[index]} rowIndex={index + firstRowIndex} columnIndex={itemIndex + firstColumnIndex} />
                  </td>
                )))}
              </tr>
            )))}
            {tableData.length === 0 && (
              <tr>
                <td colSpan="100%">
                  <p className="no-data-text">Данных нет</p>
                </td>
              </tr>
            )}
            {isFetch && Loader && (
              <tr>
                <td style={{ boxShadow: 'none' }}>
                  <Loader />
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
